import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import type { SocialMember } from './types';
import ClubBadge from './ClubBadge';
import LinkButton from './LinkButton';
import { getUserProfileById, type UserProfile } from '@/lib/coreApi';

interface MemberProfileSlideoutProps {
  userId: string;
  member?: SocialMember | null;
  clubId: string;
  currentUserId?: string;
  theme: ClubTheme;
  onClose: () => void;
  onMessage?: (userId: string) => void;
}

export default function MemberProfileSlideout({
  userId,
  member,
  clubId,
  currentUserId,
  theme,
  onClose,
  onMessage,
}: MemberProfileSlideoutProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const isOwnProfile = userId === currentUserId;

  useEffect(() => {
    setLoading(true);
    getUserProfileById(userId)
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const name = profile?.display_name || member?.user?.full_name || 'Unknown';
  const avatarUrl = profile?.avatar_url || member?.user?.avatar_url;
  const role = profile?.role || member?.user?.role || member?.club_role || 'member';
  const joinedDate = member?.joined_at
    ? new Date(member.joined_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Slideout panel */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col shadow-2xl animate-in slide-in-from-right"
        style={{ backgroundColor: theme.colors.surface }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: `${theme.colors.muted}20` }}>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.colors.muted }}>
            {isOwnProfile ? 'Your Profile' : 'Member Profile'}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 transition-colors hover:opacity-70">
            <XMarkIcon className="h-5 w-5" style={{ color: theme.colors.muted }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
              />
            </div>
          ) : (
            <>
              {/* Avatar + Name + Role */}
              <div className="flex flex-col items-center text-center">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-20 w-20 rounded-full object-cover shadow-md" />
                ) : (
                  <div
                    className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold shadow-md"
                    style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
                  >
                    {(name[0] || '?').toUpperCase()}
                  </div>
                )}
                <h3 className="mt-3 text-lg font-bold" style={{ color: theme.colors.text }}>{name}</h3>
                {profile?.tagline && (
                  <p className="mt-1 text-sm" style={{ color: theme.colors.muted }}>{profile.tagline}</p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <ClubBadge role={member?.club_role || role} theme={theme} />
                  {joinedDate && (
                    <span className="text-[11px]" style={{ color: theme.colors.muted }}>
                      Joined {joinedDate}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              {!isOwnProfile && (
                <div className="flex items-center justify-center gap-3">
                  <LinkButton clubId={clubId} targetId={userId} theme={theme} showMenu />
                  {onMessage && (
                    <button
                      type="button"
                      onClick={() => { onMessage(userId); onClose(); }}
                      className="rounded-xl px-4 py-2 text-xs font-semibold transition-colors"
                      style={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.primary }}
                    >
                      Send Message
                    </button>
                  )}
                </div>
              )}

              {/* Quote */}
              {profile?.quote_text && (
                <blockquote
                  className="rounded-xl p-4 text-center text-sm italic"
                  style={{
                    backgroundColor: `${theme.colors.primary}08`,
                    border: `1px solid ${theme.colors.primary}20`,
                    color: theme.colors.text,
                  }}
                >
                  &ldquo;{profile.quote_text}&rdquo;
                  {profile.quote_attr && (
                    <footer className="mt-2 text-xs not-italic" style={{ color: theme.colors.muted }}>
                      &mdash; {profile.quote_attr}
                    </footer>
                  )}
                </blockquote>
              )}

              {/* Bio */}
              {profile?.bio_md && (
                <div>
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.muted }}>About</h4>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: theme.colors.text }}>
                    {profile.bio_md}
                  </p>
                </div>
              )}

              {/* Location */}
              {profile?.location && (
                <div className="flex items-center gap-2 text-sm" style={{ color: theme.colors.muted }}>
                  <span>📍</span>
                  <span>{profile.location}</span>
                </div>
              )}

              {/* Links */}
              {Array.isArray(profile?.links) && (profile.links as any[]).length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.muted }}>Links</h4>
                  <div className="flex flex-wrap gap-2">
                    {(profile.links as Array<{ label: string; url: string }>).map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-full px-3 py-1 text-xs font-medium transition-colors hover:opacity-80"
                        style={{ backgroundColor: `${theme.colors.primary}15`, color: theme.colors.primary }}
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Feed entries */}
              {Array.isArray(profile?.feed_entries) && (profile.feed_entries as any[]).length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.muted }}>Updates</h4>
                  <div className="space-y-2">
                    {(profile.feed_entries as Array<{ id: string; type: string; title: string; body?: string; tag?: string }>).map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded-xl p-3"
                        style={{ backgroundColor: `${theme.colors.muted}08`, border: `1px solid ${theme.colors.muted}15` }}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: theme.colors.primary }}>
                          {entry.tag || entry.type}
                        </p>
                        <p className="text-sm font-semibold" style={{ color: theme.colors.text }}>{entry.title}</p>
                        {entry.body && <p className="mt-1 text-xs" style={{ color: theme.colors.muted }}>{entry.body}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Edit profile hint for own profile */}
              {isOwnProfile && !profile?.bio_md && !profile?.tagline && (
                <div
                  className="rounded-xl p-4 text-center text-sm"
                  style={{ backgroundColor: `${theme.colors.primary}08`, border: `1px dashed ${theme.colors.primary}30`, color: theme.colors.muted }}
                >
                  Your profile is looking a bit empty. Add a bio, tagline, and links to help other members get to know you.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
